import { animate, cubicBezier } from 'animejs';
import globalVar from '../utils/globalVar'

class Module11Collections {
  constructor() {
    let cache = {};
    let initFunc = () => {
      initCache();
      registerEvents();
    }

    let initCache = () => {
      cache.slider = document.querySelectorAll('.module-11-collections .collections');
    };

    let destroyers = new Map();

    let registerEvents = () => {
      cache.slider.forEach((slider) => {
        if (!slider.getAttribute('data-index')) {
          slider.setAttribute('data-index', 0);
        }
        if(!globalVar.mobile.matches) {
          destroyers.set(slider, initScrollSlider(slider));
        }
      });

      globalVar.mobile.addEventListener('change', () => {
        cache.slider.forEach((slider) => {
          if (!globalVar.mobile.matches) {
            destroyers.set(slider, initScrollSlider(slider));
          } else {
            destroyers.get(slider)?.();
            destroyers.delete(slider);
          }
        });
      });
    };

    let initScrollSlider = (slider) => {
      const module = slider.closest('.module-11-collections');
      const count  = slider.querySelectorAll('.collection-item').length;
      if (count <= 1) return () => {}; // single-item: nothing to navigate, scroll normally
      let isAnimating = false;

      const sliderInset = slider.getBoundingClientRect().top - module.getBoundingClientRect().top;
      const moduleHeight = module.offsetHeight;
      let inZone = false;

      // Spacer holds the module's place in flow.
      const spacer = document.createElement('div');
      spacer.style.height = (moduleHeight + window.innerHeight) + 'px';
      module.parentNode.insertBefore(spacer, module);

      // Pin module (always fixed — position is driven by updatePosition).
      module.style.position = 'fixed';
      module.style.left     = '0';
      module.style.right    = '0';
      module.style.zIndex   = '10';

      const getZone = () => {
        const spacerTop = spacer.getBoundingClientRect().top + window.scrollY;
        const zoneTop   = spacerTop + sliderInset;
        return { zoneTop, zoneEnd: zoneTop + window.innerHeight };
      };

      const updatePosition = (scrollY, zoneTop, zoneEnd) => {
        if (scrollY <= zoneTop) {
          module.style.top = (zoneTop - sliderInset - scrollY) + 'px';
        } else if (scrollY >= zoneEnd) {
          module.style.top = (zoneEnd - sliderInset - scrollY) + 'px';
        } else {
          module.style.top = -sliderInset + 'px';
        }
      };

      let exitingZone = false;
      let exitToEnd   = false;
      let lastScrollY = window.scrollY;

      // Mobile snap helpers — keep user anchored in zone so overshoot can be detected.
      let parkY = 0;
      let isProgrammaticScroll = false;
      const snapToZone = (target) => {
        isProgrammaticScroll = true;
        window.scrollTo(0, target);
        setTimeout(() => { isProgrammaticScroll = false; }, 50);
      };

      const enterZone = (scrollingDown) => {
        inZone = true;
        // lock() stops Lenis on desktop, keeping the page from scrolling through the zone.
        // On mobile Lenis doesn't run so this is a no-op; snap handles anchoring instead.
        window.__smoothScroll?.lock();
        if (!scrollingDown) slider.setAttribute('data-index', count - 1);
        const animeSlider = module.querySelector('.anime-slider_container');
        if (animeSlider && parseFloat(getComputedStyle(animeSlider).opacity) !== 1) {
          animate(animeSlider, { opacity: [parseFloat(getComputedStyle(animeSlider).opacity), 1], duration: 400, ease: 'outSine' });
        }
      };

      const exitZone = (toEnd) => {
        exitingZone = true;
        exitToEnd   = toEnd;
        inZone      = false;
        window.__smoothScroll?.unlock();
        const { zoneTop, zoneEnd } = getZone();
        window.scrollTo(0, toEnd ? Math.ceil(zoneEnd) + 1 : Math.floor(zoneTop) - 1);
        // exitingZone is cleared by position check in scroll handler for both desktop and mobile.
      };

      // ── Scroll handler ─────────────────────────────────────────────────────────
      const onScroll = () => {
        const scrollY = window.scrollY;
        const { zoneTop, zoneEnd } = getZone();
        const scrollingDown = scrollY > lastScrollY;
        lastScrollY = scrollY;

        // Module is always fixed — must update on every scroll event.
        updatePosition(scrollY, zoneTop, zoneEnd);

        // Ignore scroll events caused by our own snapToZone calls.
        if (isProgrammaticScroll) return;

        // Clear exitingZone once scrollY has crossed the correct boundary.
        if (exitingZone && ((exitToEnd && scrollY >= zoneEnd) || (!exitToEnd && scrollY < zoneTop))) {
          exitingZone = false;
        }

        let justEntered = false;
        if (!inZone && !exitingZone && scrollY >= zoneTop && scrollY < zoneEnd) {
          enterZone(scrollingDown);
          justEntered = true;
          parkY = scrollingDown ? zoneTop : zoneEnd - 1;
        }
        if (inZone && exitingZone && (scrollY < zoneTop || scrollY >= zoneEnd)) inZone = false;

        // ── Mobile: overshoot detection + snap ──────────────────────────────────
        // Lenis doesn't run on mobile so we snap to parkY to hold the user in the
        // zone, and detect scroll overshoot to trigger slide transitions.
        if (inZone && !exitingZone && globalVar.mobile.matches) {
          const overshoot = scrollY - parkY;
          const idx = parseInt(slider.getAttribute('data-index'));
          if (!isAnimating && !justEntered) {
            if (overshoot > 30) {
              if (idx < count - 1) {
                isAnimating = true; slide(slider);
                setTimeout(() => { isAnimating = false; }, 1600);
              } else {
                exitZone(true); return;
              }
            } else if (overshoot < -30) {
              if (idx > 0) {
                isAnimating = true; slideBack(slider);
                setTimeout(() => { isAnimating = false; }, 1600);
              } else {
                exitZone(false); return;
              }
            }
          }
          snapToZone(parkY);
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });

      // ── Desktop: wheel-based navigation (Lenis is locked while in zone) ────────
      const onWheel = (e) => {
        if (!inZone || globalVar.mobile.matches) return;
        e.preventDefault();
        if (isAnimating) return;

        const idx     = parseInt(slider.getAttribute('data-index'));
        const forward = e.deltaY > 0;

        if (forward) {
          if (idx < count - 1) {
            isAnimating = true;
            slide(slider);
            setTimeout(() => { isAnimating = false; }, 1900);
          } else {
            exitZone(true);
          }
        } else {
          if (idx > 0) {
            isAnimating = true;
            slideBack(slider);
            setTimeout(() => { isAnimating = false; }, 1900);
          } else {
            exitZone(false);
          }
        }
      };
      window.addEventListener('wheel', onWheel, { passive: false });

      // Initial position
      const { zoneTop: initZoneTop, zoneEnd: initZoneEnd } = getZone();
      updatePosition(window.scrollY, initZoneTop, initZoneEnd);

      return () => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('wheel', onWheel);
        window.__smoothScroll?.unlock();
        spacer.remove();
        module.style.position = '';
        module.style.left     = '';
        module.style.right    = '';
        module.style.zIndex   = '';
        module.style.top      = '';
        slider.setAttribute('data-index', 0);
      };
    };

    let slide = (slider) => {
      let index = parseInt(slider.getAttribute('data-index'));
      const contentItem = slider.querySelectorAll('.collection-item');
      const mediaItem   = slider.querySelectorAll('.collection-item-media');
      const currentContent = contentItem[index];
      const currentMedia   = mediaItem[index];

      if (index < contentItem.length - 1) {
        index++;
      } else {
        index = 0;
      }
      slider.setAttribute('data-index', index);
      const nextContent = contentItem[index];
      const nextMedia   = mediaItem[index];

      animate(currentContent, {
        opacity: [1, 0],
        scale: [1, 0.9],
        duration: 600,
        ease: cubicBezier(0.567, -0.013, 0.493, 0.98),
        onComplete: () => {
          currentContent.style.display = 'none';
          nextContent.style.display = 'flex';
          animate(nextContent, {
            opacity: [0, 1],
            scale: [0.9, 1],
            duration: 600,
            ease: cubicBezier(0.567, -0.013, 0.493, 0.98)
          });
        }
      });

      const curFwd = currentMedia.querySelector('.media-container');
      const nxtFwd = nextMedia.querySelector('.media-container');
      const swapFwd = () => {
        currentMedia.style.display = 'none';
        nextMedia.style.display = 'block';
        if (nxtFwd) animate(nxtFwd, { y: ['100%', '0%'], opacity: [0.5, 1], duration: 800, ease: cubicBezier(0.567, -0.013, 0.493, 0.98) });
      };
      if (curFwd) {
        animate(curFwd, { y: ['0%', '-100%'], opacity: [1, 0.5], duration: 800, ease: cubicBezier(0.567, -0.013, 0.493, 0.98), onComplete: swapFwd });
      } else {
        swapFwd();
      }
    };

    let slideBack = (slider) => {
      let index = parseInt(slider.getAttribute('data-index'));
      const contentItem = slider.querySelectorAll('.collection-item');
      const mediaItem   = slider.querySelectorAll('.collection-item-media');
      const currentContent = contentItem[index];
      const currentMedia   = mediaItem[index];

      if (index > 0) {
        index--;
      } else {
        index = contentItem.length - 1;
      }
      slider.setAttribute('data-index', index);
      const prevContent = contentItem[index];
      const prevMedia   = mediaItem[index];

      animate(currentContent, {
        opacity: [1, 0],
        scale: [1, 0.9],
        duration: 600,
        ease: cubicBezier(0.567, -0.013, 0.493, 0.98),
        onComplete: () => {
          currentContent.style.display = 'none';
          prevContent.style.display = 'flex';
          animate(prevContent, {
            opacity: [0, 1],
            scale: [0.9, 1],
            duration: 600,
            ease: cubicBezier(0.567, -0.013, 0.493, 0.98)
          });
        }
      });

      const curBck = currentMedia.querySelector('.media-container');
      const prvBck = prevMedia.querySelector('.media-container');
      const swapBck = () => {
        currentMedia.style.display = 'none';
        prevMedia.style.display = 'block';
        if (prvBck) animate(prvBck, { y: ['-100%', '0%'], opacity: [0.5, 1], duration: 800, ease: cubicBezier(0.567, -0.013, 0.493, 0.98) });
      };
      if (curBck) {
        animate(curBck, { y: ['0%', '100%'], opacity: [1, 0.5], duration: 800, ease: cubicBezier(0.567, -0.013, 0.493, 0.98), onComplete: swapBck });
      } else {
        swapBck();
      }
    };

    initFunc();
  };
}
export { Module11Collections }
